import { useRouter } from "found";
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { graphql } from "react-relay";

import BanModal, {
  UpdateType,
} from "coral-admin/components/UserStatus/BanModal";
import {
  ApproveCommentMutation,
  ApproveImageMutation,
  RejectCommentMutation,
  RejectImageMutation,
} from "coral-admin/mutations";
import FadeInTransition from "coral-framework/components/FadeInTransition";
import { getModerationLink } from "coral-framework/helpers";
import parseModerationOptions from "coral-framework/helpers/parseModerationOptions";
import { globalErrorReporter } from "coral-framework/lib/errors";
import {
  useLocal,
  useMutation,
  withFragmentContainer,
} from "coral-framework/lib/relay";
import { GQLSTORY_MODE, GQLTAG, GQLUSER_STATUS } from "coral-framework/schema";

import {
  COMMENT_STATUS,
  ModerateCardContainer_comment,
} from "coral-admin/__generated__/ModerateCardContainer_comment.graphql";
import { ModerateCardContainer_settings } from "coral-admin/__generated__/ModerateCardContainer_settings.graphql";
import { ModerateCardContainer_viewer } from "coral-admin/__generated__/ModerateCardContainer_viewer.graphql";
import { ModerateCardContainerLocal } from "coral-admin/__generated__/ModerateCardContainerLocal.graphql";

import RemoveUserBanMutation from "../UserStatus/RemoveUserBanMutation";
import UpdateUserBanMutation from "../UserStatus/UpdateUserBanMutation";
import BanCommentUserMutation from "./BanCommentUserMutation";
import FeatureCommentMutation from "./FeatureCommentMutation";
import ModerateCard from "./ModerateCard";
import ModeratedByContainer from "./ModeratedByContainer";
import UnfeatureCommentMutation from "./UnfeatureCommentMutation";

interface Props {
  viewer: ModerateCardContainer_viewer;
  comment: ModerateCardContainer_comment;
  settings: ModerateCardContainer_settings;
  danglingLogic: (status: COMMENT_STATUS) => boolean;
  showStoryInfo: boolean;
  mini?: boolean;
  hideUsername?: boolean;
  onUsernameClicked?: (userID: string, username: string) => void;
  onConversationClicked?: (commentID: string) => void;
  onSetSelected?: () => void;
  selected?: boolean;
  selectPrev?: () => void;
  selectNext?: () => void;
  loadNext?: (() => void) | null;
}

function getUserName({
  siteUsername,
  author,
}: ModerateCardContainer_comment): string {
  const primarySiteUsername = siteUsername?.[0];
  const { name } = primarySiteUsername || {};
  if (name) {
    return name;
  }

  const { username } = author || {};
  if (username) {
    return username;
  }

  return "";
}

function isFeatured(comment: ModerateCardContainer_comment) {
  return comment.tags.some((t) => t.code === GQLTAG.FEATURED);
}

function isQATagged(comment: ModerateCardContainer_comment) {
  return comment.tags.some((t) => t.code === GQLTAG.QA);
}

const ModerateCardContainer: FunctionComponent<Props> = ({
  comment,
  settings,
  viewer,
  danglingLogic,
  showStoryInfo,
  mini,
  hideUsername,
  selected,
  selectPrev,
  selectNext,
  onUsernameClicked: usernameClicked,
  onConversationClicked: conversationClicked,
  onSetSelected: setSelected,
  loadNext,
}) => {
  const approveComment = useMutation(ApproveCommentMutation);
  const rejectComment = useMutation(RejectCommentMutation);
  const approveImage = useMutation(ApproveImageMutation);
  const rejectImage = useMutation(RejectImageMutation);
  const featureComment = useMutation(FeatureCommentMutation);
  const unfeatureComment = useMutation(UnfeatureCommentMutation);
  const banUser = useMutation(BanCommentUserMutation);
  const updateUserBan = useMutation(UpdateUserBanMutation);
  const removeUserBan = useMutation(RemoveUserBanMutation);

  const { match, router } = useRouter();

  const [{ moderationQueueSort }] = useLocal<
    ModerateCardContainerLocal
  >(graphql`
    fragment ModerateCardContainerLocal on Local {
      moderationQueueSort
    }
  `);

  const scoped = useMemo(() => !!viewer.moderationScopes?.scoped, [viewer]);

  const readOnly = useMemo(() => scoped && !comment.canModerate, [
    scoped,
    comment,
  ]);

  const [showBanModal, setShowBanModal] = useState(false);

  const handleApprove = useCallback(async () => {
    if (!comment.revision) {
      return;
    }

    if (readOnly) {
      return;
    }

    const { storyID, siteID, section } = parseModerationOptions(match);

    await approveComment({
      commentID: comment.id,
      commentRevisionID: comment.revision.id,
      storyID,
      siteID,
      section,
      orderBy: moderationQueueSort,
    });
    if (loadNext) {
      loadNext();
    }
  }, [
    approveComment,
    comment.id,
    comment.revision,
    loadNext,
    match,
    readOnly,
    moderationQueueSort,
  ]);

  const handleReject = useCallback(async () => {
    if (!comment.revision) {
      return;
    }

    if (readOnly) {
      return;
    }

    const { storyID, siteID, section } = parseModerationOptions(match);

    await rejectComment({
      commentID: comment.id,
      commentRevisionID: comment.revision.id,
      storyID,
      siteID,
      section,
      orderBy: moderationQueueSort,
    });
    if (loadNext) {
      loadNext();
    }
  }, [
    comment.revision,
    comment.id,
    readOnly,
    match,
    rejectComment,
    loadNext,
    moderationQueueSort,
  ]);

  const handleImageApprove = useCallback(
    async (imageID: string) => {
      if (readOnly) {
        return;
      }

      await approveImage({
        commentID: comment.id,
        imageID,
      });
      if (loadNext) {
        loadNext();
      }
    },
    [approveImage, loadNext, comment.id, readOnly]
  );

  const handleImageReject = useCallback(
    async (imageID: string) => {
      if (readOnly) {
        return;
      }

      await rejectImage({
        commentID: comment.id,
        imageID,
      });
      await handleReject();
      if (loadNext) {
        loadNext();
      }
    },
    [rejectImage, loadNext, handleReject, comment.id, readOnly]
  );

  const handleFeature = useCallback(
    (tag: GQLTAG, label?: string) => {
      if (!comment.revision) {
        return;
      }

      if (readOnly) {
        return;
      }

      const { storyID, siteID, section } = parseModerationOptions(match);

      void featureComment({
        commentID: comment.id,
        commentRevisionID: comment.revision.id,
        storyID: storyID || comment?.story?.id,
        siteID,
        section,
        orderBy: moderationQueueSort,
        label,
        tag,
      });
    },
    [featureComment, comment, match, readOnly, moderationQueueSort]
  );

  const handleUnfeature = useCallback(
    (tag: GQLTAG) => {
      if (readOnly) {
        return;
      }

      void unfeatureComment({
        commentID: comment.id,
        storyID: match.params.storyID,
        tag,
      });
    },
    [unfeatureComment, comment, match, readOnly]
  );

  const onFeature = useCallback(
    (tag: GQLTAG, label?: string) => {
      if (readOnly) {
        return;
      }

      const featured = isFeatured(comment);
      const qaTagged = isQATagged(comment);

      if (featured || qaTagged) {
        handleUnfeature(tag);
      } else {
        handleFeature(tag, label);
      }
    },
    [comment, readOnly, handleFeature, handleUnfeature]
  );

  const onUsernameClicked = useCallback(
    (id?: string) => {
      if (!usernameClicked) {
        return;
      }
      usernameClicked(id || comment.author!.id, getUserName(comment));
    },
    [usernameClicked, comment]
  );

  const onConversationClicked = useCallback(() => {
    if (!conversationClicked) {
      return;
    }
    conversationClicked(comment.id);
  }, [conversationClicked, comment]);

  const handleModerateStory = useCallback(
    (e: React.MouseEvent) => {
      router.push(getModerationLink({ storyID: comment.story.id }));
      if (e.preventDefault) {
        e.preventDefault();
      }
    },
    [router, comment]
  );

  const onFocusOrClick = useCallback(() => {
    if (setSelected) {
      setSelected();
    }
  }, [setSelected]);

  const handleBanModalClose = useCallback(() => {
    setShowBanModal(false);
  }, [setShowBanModal]);

  const openBanModal = useCallback(() => {
    if (
      !comment.author ||
      comment.author.status.current.includes(GQLUSER_STATUS.BANNED)
    ) {
      return;
    }

    setShowBanModal(true);
  }, [comment, setShowBanModal]);

  const handleBanConfirm = useCallback(
    async (
      updateType: UpdateType,
      rejectExistingComments: boolean,
      banSiteIDs: string[] | null | undefined,
      unbanSiteIDs: string[] | null | undefined,
      message: string
    ) => {
      const viewerIsScoped = !!viewer.moderationScopes?.sites?.length;
      switch (updateType) {
        case UpdateType.ALL_SITES:
          await banUser({
            userID: comment.author!.id, // Should be defined because the modal shouldn't open if author is null
            message,
            rejectExistingComments,
            siteIDs: viewerIsScoped
              ? viewer.moderationScopes!.sites!.map(({ id }) => id)
              : [],
          });
          break;
        case UpdateType.SPECIFIC_SITES:
          await updateUserBan({
            userID: comment.author!.id,
            message,
            banSiteIDs,
            unbanSiteIDs,
          });
          break;
        case UpdateType.NO_SITES:
          await removeUserBan({
            userID: comment.author!.id,
          });
      }
      setShowBanModal(false);
    },
    [comment, banUser, setShowBanModal, removeUserBan, updateUserBan, viewer]
  );

  // Only highlight comments that have been flagged for containing a banned or
  // suspect word.
  const highlight = useMemo(() => {
    if (!comment.revision) {
      return false;
    }

    if (!comment.revision.actionCounts) {
      throw new Error(`action counts missing: ${comment.id}`);
    }

    const count =
      comment.revision.actionCounts.flag.reasons.COMMENT_DETECTED_BANNED_WORD +
      comment.revision.actionCounts.flag.reasons.COMMENT_DETECTED_SUSPECT_WORD;

    return count > 0;
  }, [comment]);

  // const isRatingsAndReviews =
  //   comment.story.settings.mode === GQLSTORY_MODE.RATINGS_AND_REVIEWS;

  let meta: any = {};
  try {
    if (comment && comment.meta) {
      meta = JSON.parse(comment.meta);
    }
  } catch (err) {
    globalErrorReporter.report(err);
  }

  const inReplyTo = comment.parent
    ? comment.parent.siteUsername?.[0]?.name ||
      comment.parent.author?.username ||
      comment.parent.author?.id
    : null;

  return (
    <>
      <FadeInTransition active={!!comment.enteredLive}>
        <ModerateCard
          id={comment.id}
          username={getUserName(comment)}
          createdAt={comment.createdAt}
          body={comment.body!}
          rating={comment.rating ?? null}
          ratingScale={comment.ratingScale}
          highlight={highlight}
          inReplyTo={inReplyTo}
          comment={comment}
          settings={settings}
          dangling={danglingLogic(comment.status)}
          status={
            comment.status === "APPROVED"
              ? "approved"
              : comment.status === "REJECTED"
              ? "rejected"
              : "undecided"
          }
          featured={isFeatured(comment)}
          qaTagged={isQATagged(comment)}
          viewContextHref={comment.permalink}
          onApprove={handleApprove}
          onReject={handleReject}
          onImageApprove={handleImageApprove}
          onImageReject={handleImageReject}
          onFeature={onFeature}
          onUsernameClick={onUsernameClicked}
          onConversationClick={
            conversationClicked ? onConversationClicked : null
          }
          selected={selected}
          selectPrev={selectPrev}
          selectNext={selectNext}
          siteName={settings.multisite ? comment.site.name : null}
          onBan={openBanModal}
          moderatedBy={
            <ModeratedByContainer
              onUsernameClicked={onUsernameClicked}
              comment={comment}
            />
          }
          onFocusOrClick={onFocusOrClick}
          showStory={showStoryInfo}
          meta={meta}
          storyHref={getModerationLink({ storyID: comment.story.id })}
          onModerateStory={handleModerateStory}
          mini={mini}
          hideUsername={hideUsername}
          deleted={comment.deleted ? comment.deleted : false}
          edited={comment.editing.edited}
          readOnly={readOnly}
          isQA={comment.story.settings.mode === GQLSTORY_MODE.QA}
          bannedWords={comment.revision?.metadata?.wordList?.bannedWords || []}
          suspectWords={
            comment.revision?.metadata?.wordList?.suspectWords || []
          }
          isArchived={comment.story.isArchived}
          isArchiving={comment.story.isArchiving}
          images={
            comment.images
              ? [...comment.images.filter((img) => img !== null)]
              : []
          }
          commentTagLabel={comment?.story?.commentTagLabel}
        />
      </FadeInTransition>
      {comment.author && (
        <BanModal
          username={
            comment.author && comment.author.username
              ? comment.author.username
              : ""
          }
          open={showBanModal}
          onClose={handleBanModalClose}
          onConfirm={handleBanConfirm}
          viewerScopes={{
            role: viewer.role,
            sites: viewer.moderationScopes?.sites?.map((s) => s),
          }}
          moderationScopesEnabled={settings.multisite}
          userBanStatus={comment.author.status.ban}
          userRole={comment.author.role}
        />
      )}
    </>
  );
};

const enhanced = withFragmentContainer<Props>({
  comment: graphql`
    fragment ModerateCardContainer_comment on CoralComment {
      id
      author {
        id
        username
        status {
          current
          ban {
            active
            sites {
              id
              name
            }
          }
        }
        role
      }
      siteUsername {
        siteID
        name
      }
      statusLiveUpdated
      createdAt
      body
      meta
      rating
      ratingScale
      revision {
        actionCounts {
          flag {
            reasons {
              COMMENT_DETECTED_BANNED_WORD
              COMMENT_DETECTED_SUSPECT_WORD
            }
          }
        }
        metadata {
          wordList {
            bannedWords {
              value
              index
              length
            }
            suspectWords {
              value
              index
              length
            }
          }
        }
      }
      tags {
        code
      }
      status
      revision {
        id
      }
      editing {
        edited
      }
      parent {
        author {
          id
          username
        }
        siteUsername {
          siteID
          name
        }
      }
      canModerate
      story {
        id
        metadata {
          title
        }
        settings {
          mode
        }
        isArchived
        isArchiving
        commentTagLabel {
          FEATURED
          QA
        }
      }
      site {
        id
        name
      }
      permalink
      enteredLive
      deleted
      images {
        id
        url
        status
        reasons
      }
      ...MarkersContainer_comment
      ...ModeratedByContainer_comment
      ...CommentAuthorContainer_comment
      ...MediaContainer_comment
    }
  `,
  settings: graphql`
    fragment ModerateCardContainer_settings on Settings {
      locale
      wordList {
        banned
        suspect
      }
      multisite
      enableQaFlag
      qaFlagEnabledSites
      ...MarkersContainer_settings
    }
  `,
  viewer: graphql`
    fragment ModerateCardContainer_viewer on CoralUser {
      role
      moderationScopes {
        scoped
        sites {
          id
          name
        }
      }
    }
  `,
})(ModerateCardContainer);

export default enhanced;
